/**
 * Export utilities for Log Viewer
 * 
 * Provides CSV and JSON export functionality for filtered logs.
 */

import type { LogEvent } from '../../../utils/WeBelieveLogger.types';

function downloadFile(content: string, mimeType: string, filename: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatTimestamp(): string {
  const now = new Date();
  return now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Export logs to CSV format
 */
export function exportToCSV(logs: LogEvent[]): void {
  const headers = ['Timestamp', 'Level', 'Component', 'Operation', 'Message', 'Duration (ms)', 'Session ID', 'User ID', 'Document ID', 'Data'];
  
  const rows = logs.map(log => [
    new Date(log.timestamp).toISOString(),
    log.level,
    log.component,
    log.operation || '',
    log.message,
    log.duration_ms?.toString() || '',
    log.session_id,
    log.user_id || '',
    log.document_id || '',
    log.data ? JSON.stringify(log.data) : '',
  ].map(escapeCSV));

  const csv = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const filename = `edgelex-logs-${formatTimestamp()}.csv`;
  downloadFile(csv, 'text/csv;charset=utf-8', filename);
}

/**
 * Export logs to JSON format
 */
export function exportToJSON(logs: LogEvent[]): void {
  const json = JSON.stringify(logs, null, 2);
  const filename = `edgelex-logs-${formatTimestamp()}.json`;
  downloadFile(json, 'application/json', filename);
}

/**
 * Export a single log to JSON
 */
export function exportSingleLogToJSON(log: LogEvent): void {
  const json = JSON.stringify(log, null, 2);
  const filename = `edgelex-log-${log.session_id.slice(0, 8)}-${formatTimestamp()}.json`;
  downloadFile(json, 'application/json', filename);
}
